// ─────────────────────────────────────────────────────────────────────────────
// Base Entity Utilities - TypeScript Implementation
// Utility functions for handling base entity fields with type safety
// ─────────────────────────────────────────────────────────────────────────────

import { v4 as uuidv4 } from "uuid";
import { BaseEntity } from "../types";

// Get current UTC timestamp in ISO format
export function nowIsoUtc(): string {
  return new Date().toISOString();
}

// Generate a new GUID using native crypto API when available, fallback to uuid library
export function newGuid(): string {
  // Check for native crypto API support
  try {
    // Try window.crypto first (browser environment)
    if (
      typeof window !== "undefined" &&
      window.crypto &&
      window.crypto.randomUUID
    ) {
      return window.crypto.randomUUID();
    }
    // Try global crypto (Node.js environment)
    if (
      typeof global !== "undefined" &&
      global.crypto &&
      global.crypto.randomUUID
    ) {
      return global.crypto.randomUUID();
    }
  } catch (e) {
    // If crypto API fails, fall back to uuidv4
    console.warn(
      "Native crypto.randomUUID not available, falling back to uuid library"
    );
  }
  return uuidv4();
}

// Add base creation fields to an entity with type safety
export function withBaseCreateFields<T>(
  entity?: T,
  createdByUserId?: string | null
): T &
  BaseEntity & {
    CreatedByUserId?: string | null;
    ModifiedByUserId?: string | null;
  } {
  const Id = (entity as any)?.Id || newGuid();

  return {
    ...entity,
    Id,
    Deactivated: false,
    CreatedDateTime: nowIsoUtc(),
    ModifiedDateTime: null,
    Synced: false,
    CreatedByUserId: createdByUserId || null,
    ModifiedByUserId: null,
  } as T &
    BaseEntity & {
      CreatedByUserId?: string | null;
      ModifiedByUserId?: string | null;
    };
}

// Add base update fields to an entity with type safety
export function withBaseUpdateFields<T>(
  entity: T,
  modifiedByUserId?: string | null
): T & {
  ModifiedDateTime: string;
  Synced: boolean;
  ModifiedByUserId?: string | null;
} {
  return {
    ...entity,
    ModifiedDateTime: nowIsoUtc(),
    Synced: false,
    ModifiedByUserId: modifiedByUserId || null,
  } as T & {
    ModifiedDateTime: string;
    Synced: boolean;
    ModifiedByUserId?: string | null;
  };
}

// Utility to check if an entity is deactivated
export function isDeactivated(entity: BaseEntity): boolean {
  return entity.Deactivated === true;
}

// Utility to check if an entity is synced
export function isSynced(entity: BaseEntity): boolean {
  return entity.Synced === true;
}

// Utility to get entity age in milliseconds
export function getEntityAge(entity: BaseEntity): number {
  const created = new Date(entity.CreatedDateTime);
  return Date.now() - created.getTime();
}

// Utility to format entity timestamp for display
export function formatEntityTimestamp(timestamp: string | null): string | null {
  if (!timestamp) return null;

  try {
    return new Date(timestamp).toLocaleString();
  } catch (error) {
    console.warn("Invalid timestamp format:", timestamp);
    return null;
  }
}

// Type guard to check if object has base entity properties
export function isBaseEntity(obj: any): obj is BaseEntity {
  return (
    obj &&
    typeof obj === "object" &&
    typeof obj.Id === "string" &&
    typeof obj.CreatedDateTime === "string" &&
    typeof obj.Deactivated === "boolean"
  );
}

// Utility to create a new entity with base fields
export function createNewEntity<T>(
  data: T,
  createdByUserId?: string | null
): T &
  BaseEntity & {
    CreatedByUserId?: string | null;
    ModifiedByUserId?: string | null;
  } {
  return withBaseCreateFields(data, createdByUserId);
}

// Utility to clone an entity with new ID
export function cloneEntity<T extends BaseEntity>(
  entity: T,
  createdByUserId?: string | null
): T & { CreatedByUserId?: string | null; ModifiedByUserId?: string | null } {
  const { Id, CreatedDateTime, ModifiedDateTime, ...data } = entity;
  return withBaseCreateFields(data as Partial<T>, createdByUserId) as T & {
    CreatedByUserId?: string | null;
    ModifiedByUserId?: string | null;
  };
}
